import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import DomainAddIcon from "@mui/icons-material/DomainAdd";
import EditIcon from "@mui/icons-material/Edit";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ExitIcon from "@mui/icons-material/TransitEnterexit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import { styled } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import RowLink from "_shared/_views/tables/row-link";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import Guid from "_shared/guids/guid";
import * as Config from "config";
import CompanyAvatar from "marketplace/_views/avatars/company-avatar";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import TeamAvatar from "marketplace/_views/avatars/team-avatar";
import { PageTab } from "marketplace/_views/profiles/profiles-page-tabs";
import { ProfileTableData } from "marketplace/_views/profiles/profile-table-data";
import VisibilityToggleSwitch from "marketplace/_views/profiles/visibility-toggle-switch";
import Company from "marketplace/companies/company";
import Individual from "marketplace/individuals/individual";
import Team from "marketplace/teams/team";
import {
  createMRTColumnHelper,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_PaginationState as PaginationState,
  type MRT_SortingState as SortingState,
  type MRT_TableOptions,
} from "material-react-table";
import {
  MobileToolbarButtonsContainer,
  TableTitleRow,
  ScrollableTabs,
  searchFieldSpacing,
  searchTextFieldProps,
  topToolbarFitsThePage,
  ToolbarButtons,
} from "_shared/_views/tables/table-toolbar";
import React, { JSX, useCallback, useMemo } from "react";

const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const TableTopToolbarContainer = styled("div")(() => ({
  maxWidth: "100%",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));
const LowerTopToolbarContainer = styled("div")(({ theme }) => ({
  minWidth: 0,
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  display: "flex",
  flexDirection: "row",
  alignItems: "left",
  width: "100%",
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
const NewProfileButtonContainer = styled("span")<{ disabled: boolean }>(({ disabled }) => ({
  cursor: disabled ? "not-allowed" : "default",
}));
const NameCellContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));
const CategoryChip = styled(Chip)(({ theme }) => ({
  color: "#777",
  marginRight: theme.spacing(0.4),
}));
const RowTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.1em",
  fontWeight: "600",
  marginLeft: theme.spacing(1.5),
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const NewButton = styled(IconButton)(() => ({
  padding: 0,
}));

const columnHelper = createMRTColumnHelper<ProfileTableData>();

type MarketplaceProfilesTableProps = {
  activeTab?: PageTab;
  addProfileDisabled: boolean;
  addProfileDisabledMessage: string;
  canViewTeamProfiles: boolean;
  currentUserId?: Guid;
  getShouldDisableRowEditing: (profile: ProfileTableData) => boolean;
  isError: boolean;
  isLoading: boolean;
  isMediumDisplaySize: boolean;
  onCreateProfile: () => void;
  onDeleteProfile: (profile: ProfileTableData) => void;
  onEditProfile: (profile: ProfileTableData) => void;
  onLeaveTeam: (profile: ProfileTableData) => void;
  onPublishProfile: (profile: ProfileTableData) => void;
  onRetry: () => void;
  onTabChange: (tab: PageTab) => void;
  profiles: ProfileTableData[];
  rowCount: number;
  showSkeletons: boolean;
  viewingAsVendor?: boolean;
};

const newProfileIcon = (activeTab: PageTab) => {
  if (activeTab === PageTab.Individual) return <PersonAddIcon fontSize="large" />;
  if (activeTab === PageTab.Company) return <DomainAddIcon fontSize="large" />;
  if (activeTab === PageTab.Teams) return <GroupAddIcon fontSize="large" />;
};

export default function MarketplaceProfilesTable(props: Readonly<MarketplaceProfilesTableProps>) {
  const {
    activeTab,
    addProfileDisabled,
    addProfileDisabledMessage,
    canViewTeamProfiles,
    currentUserId,
    getShouldDisableRowEditing,
    isError,
    isLoading,
    isMediumDisplaySize,
    onCreateProfile,
    onDeleteProfile,
    onEditProfile,
    onLeaveTeam,
    onPublishProfile,
    onRetry,
    onTabChange,
    profiles,
    rowCount,
    showSkeletons,
    viewingAsVendor,
  } = props;

  const [query, setQuery] = React.useState("");
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const renderNameColumn = useCallback((profileData: ProfileTableData): JSX.Element => {
    return (
      <NameCellContainer>
        {profileData instanceof Individual && <IndividualAvatar individual={profileData ?? null} />}
        {profileData instanceof Company && <CompanyAvatar company={profileData} />}
        {profileData instanceof Team && <TeamAvatar team={profileData} />}
        <RowTitle>
          <RowLink onClick={() => onEditProfile(profileData)}>
            {profileData instanceof Individual
              ? `${profileData?.profile?.firstName} ${profileData?.profile?.lastName}`
              : profileData.profile?.name}
          </RowLink>
        </RowTitle>
      </NameCellContainer>
    );
  }, [onEditProfile]);

  const renderCategoriesColumn = useCallback((profileData: ProfileTableData): JSX.Element => {
    if (!profileData.profile?.categories || profileData.profile.categories.length < 1) {
      return <Icon>remove</Icon>;
    }
    return (
      <div>
        {profileData.profile?.categories?.map((label: string) => (
          <CategoryChip key={label} label={label} size="small" />
        ))}
      </div>
    );
  }, []);

  const renderVisibilityColumn = useCallback(
    (profile: ProfileTableData): JSX.Element => {
      return (
        <Tooltip title={profile.isVisible ? "Profile is searchable on Marketplace" : "Profile is hidden from Marketplace"}>
          <span>
            <VisibilityToggleSwitch
              checked={profile.isVisible}
              disabled={getShouldDisableRowEditing(profile)}
              ariaLabel={profile.isVisible ? "Hide profile from Marketplace" : "Show profile on Marketplace"}
              stopPropagation
              onChange={() => onPublishProfile(profile)}
            />
          </span>
        </Tooltip>
      );
    },
    [getShouldDisableRowEditing, onPublishProfile],
  );

  const getMenuItems = useCallback(
    (profile: ProfileTableData): React.ReactNode[] => {
      const nodes: React.ReactNode[] = [];
      if (viewingAsVendor) {
        nodes.push(
          <MenuItem
            key="publish"
            onClick={(event) => {
              event.stopPropagation();
              onPublishProfile(profile);
            }}
          >
            <ListItemIcon>
              {profile.isVisible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText>{profile.isVisible ? "Hide From Marketplace" : "Show On Marketplace"}</ListItemText>
          </MenuItem>,
        );
      }

      nodes.push(
        <MenuItem key="edit" onClick={() => onEditProfile(profile)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Profile</ListItemText>
        </MenuItem>,
      );

      if (activeTab === PageTab.Teams && profile instanceof Team) {
        if (profile.leader?.userId?.isEqualTo(currentUserId)) {
          nodes.push(
            <MenuItem key="delete" onClick={() => onDeleteProfile(profile)}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>,
          );
        } else {
          nodes.push(
            <MenuItem key="leave" onClick={() => onLeaveTeam(profile)}>
              <ListItemIcon>
                <ExitIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Leave Team</ListItemText>
            </MenuItem>,
          );
        }
      }

      return nodes;
    },
    [activeTab, currentUserId, onDeleteProfile, onEditProfile, onLeaveTeam, onPublishProfile, viewingAsVendor],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor(renderNameColumn, {
        header: "Name",
        id: "name",
      }),
      columnHelper.accessor(renderCategoriesColumn, {
        header: "Categories",
        id: "categories",
      }),
      columnHelper.accessor(renderVisibilityColumn, {
        header: "Searchable On Marketplace?",
        id: "isVisible",
      }),
    ],
    [renderCategoriesColumn, renderNameColumn, renderVisibilityColumn],
  );

  const tableOptions: MRT_TableOptions<ProfileTableData> = {
    columns,
    data: profiles,
    enableRowSelection: false,
    enableTableHead: true,
    // Deleting a team and leaving one live nowhere else in the UI — `deleteProfile` and `leaveTeam`
    // are wired up in `profile-type-page` and reach the user through this menu alone. Turning row
    // actions off left both unreachable while the column stayed pinned below, which is what gave the
    // "built, disabled, never removed" look.
    enableRowActions: true,
    enableColumnPinning: true,
    initialState: {
      showGlobalFilter: true,
      columnPinning: { right: ["mrt-row-actions"] },
    },
    positionGlobalFilter: "none",
    muiSearchTextFieldProps: ({ table }) =>
      searchTextFieldProps({ searches: "profiles", table }),
    renderEmptyRowsFallback: emptyRowsFallback({
      isError,
      isLoading,
      onRetry,
      subject: "profiles",
    }),
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <GlobalFilterTextField table={table} sx={searchFieldSpacing} />
              <ToggleFiltersButton table={table} />
              <ShowHideColumnsButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
            <Tooltip title={addProfileDisabledMessage}>
              <span>
                <NewProfileButtonContainer disabled={addProfileDisabled}>
                  <Button
                    color="primary"
                    disabled={addProfileDisabled}
                    startIcon={activeTab ? newProfileIcon(activeTab) : null}
                    onClick={onCreateProfile}
                  >
                    {Config.defaultProfileName}
                  </Button>
                </NewProfileButtonContainer>
              </span>
            </Tooltip>
          </ToolbarButtonsContainer>
        )}
      </>
    ),
    renderTopToolbarCustomActions: ({ table }) => {
      if (!activeTab) return null;
      return (
        <TableTopToolbarContainer>
          <TableTitleRow>
            <TableTitle variant="h4">Marketplace Profiles</TableTitle>
            {isMediumDisplaySize && (
              <Tooltip title={addProfileDisabledMessage}>
                <span>
                  <NewProfileButtonContainer disabled={addProfileDisabled}>
                    <NewButton color="primary" disabled={addProfileDisabled} onClick={onCreateProfile}>
                      {newProfileIcon(activeTab)}
                    </NewButton>
                  </NewProfileButtonContainer>
                </span>
              </Tooltip>
            )}
          </TableTitleRow>
          <LowerTopToolbarContainer>
            <ScrollableTabs
              value={activeTab}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              onChange={(_event, newValue: PageTab) => onTabChange(newValue)}
            >
              <Tab value={PageTab.Individual} label="Individual Profile" />
              <Tab value={PageTab.Company} label="Company Profile" />
              {canViewTeamProfiles && <Tab value={PageTab.Teams} label="Team Profiles" />}
            </ScrollableTabs>
          </LowerTopToolbarContainer>
          {isMediumDisplaySize && (
            <MobileToolbarButtonsContainer>
              <GlobalFilterTextField table={table} />
              <ToolbarButtons>
                <ToggleFiltersButton table={table} />
                <ShowHideColumnsButton table={table} />
                <ToggleFullScreenButton table={table} />
              </ToolbarButtons>
            </MobileToolbarButtonsContainer>
          )}
        </TableTopToolbarContainer>
      );
    },
    renderRowActionMenuItems: ({ row }) => getMenuItems(row.original),
    getRowId: (row) => row.id?.value ?? Guid.generate().value,
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnFilters: true,
    manualPagination: true,
    manualSorting: true,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setQuery,
    onPaginationChange: setPagination,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => onEditProfile(row.original),
      sx: { cursor: "pointer" },
    }),
    onSortingChange: setSorting,
    muiTableContainerProps: ({ table }) => ({
      sx: {
        height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight}px - ${table.refs.bottomToolbarRef.current?.offsetHeight}px)`,
      },
    }),
    muiTopToolbarProps: { sx: topToolbarFitsThePage },
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    muiToolbarAlertBannerProps: isError
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    rowCount,
    state: {
      columnFilters,
      globalFilter: query,
      isLoading,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isLoading,
      sorting,
      showSkeletons,
    },
  };

  return <MRTNoMemo<ProfileTableData> options={tableOptions} />;
}
